import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tray, Clock, Warning, MapPin, CheckCircle, Plus, User } from "@phosphor-icons/react";

const COLUMNS = [
  { key: "new", label: "New" },
  { key: "assigned", label: "Assigned" },
  { key: "in_progress", label: "In Progress" },
  { key: "resolved", label: "Resolved" },
];
const PRIORITY = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  normal: "bg-sky-100 text-sky-700 border-sky-200",
  low: "bg-gray-100 text-gray-600 border-gray-200",
};
const NEXT = { new: "in_progress", assigned: "in_progress", in_progress: "resolved" };

export default function SoysosTickets() {
  const qc = useQueryClient();
  const [dept, setDept] = useState("all");
  const { data: tickets = [] } = useQuery({ queryKey: ["sos-tickets"], queryFn: async () => (await api.get("/soysos/tickets")).data, refetchInterval: 5000 });
  const { data: stats } = useQuery({ queryKey: ["sos-stats"], queryFn: async () => (await api.get("/soysos/tickets/stats")).data, refetchInterval: 5000 });
  const { data: config } = useQuery({ queryKey: ["sos-config"], queryFn: async () => (await api.get("/soysos/config")).data });

  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.post(`/soysos/tickets/${id}/status`, { status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sos-tickets"] }); qc.invalidateQueries({ queryKey: ["sos-stats"] }); },
  });
  const assign = useMutation({
    mutationFn: async ({ id, assignee }) => (await api.post(`/soysos/tickets/${id}/assign`, { assignee })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sos-tickets"] }); toast.success("Assigned"); },
  });

  const agents = config?.agents || [];
  const workers = config?.workers || [];
  const depts = config?.departments || [];
  const requestTypes = config?.request_types || [];
  const filtered = dept === "all" ? tickets : tickets.filter((t) => t.department === dept);
  const assigneeValue = (t) => t.assigned_worker_id ? `worker:${t.assigned_worker_id}` : t.assigned_to ? `user:${t.assigned_to}` : "unassigned";

  return (
    <Page>
      <PageHeader title="Guest Requests" subtitle="Live service tickets from chat, voice, SMS & email — routed by AI.">
        <div className="flex items-center gap-2">
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="w-48" data-testid="sos-dept-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {depts.map((d) => <SelectItem key={d.key} value={d.key}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <NewRequestDialog requestTypes={requestTypes} agents={agents} workers={workers}
            onDone={() => { qc.invalidateQueries({ queryKey: ["sos-tickets"] }); qc.invalidateQueries({ queryKey: ["sos-stats"] }); }} />
        </div>
      </PageHeader>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Open requests" value={stats?.open ?? "—"} icon={Tray} />
        <Stat label="SLA breached" value={stats?.sla_breached ?? "—"} icon={Warning} tone={stats?.sla_breached ? "danger" : "ok"} />
        <Stat label="Total today" value={stats?.total ?? "—"} icon={CheckCircle} />
      </div>

      <div className="grid grid-cols-4 gap-3" data-testid="sos-board">
        {COLUMNS.map((col) => {
          const items = filtered.filter((t) => t.status === col.key || (col.key === "assigned" && t.status === "escalated"));
          return (
            <div key={col.key} className="bg-secondary/40 rounded-lg border border-border p-2.5" data-testid={`sos-col-${col.key}`}>
              <div className="flex items-center justify-between px-1 mb-2">
                <span className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">{col.label}</span>
                <span className="text-[11px] text-muted-foreground bg-background rounded-full px-2 py-0.5 border border-border">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((t) => (
                  <div key={t.id} data-testid={`sos-ticket-${t.id}`} className="bg-card rounded-lg border border-border p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[13px] font-semibold leading-snug">{t.quantity > 1 ? `${t.quantity}× ` : ""}{t.request_type}</div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 ${PRIORITY[t.priority] || PRIORITY.normal}`}>{t.priority}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1.5">
                      <span className="inline-flex items-center gap-1"><MapPin size={12} /> Room {t.room_number}</span>
                      <span>· {t.department}</span>
                    </div>
                    {t.details && <div className="text-[11.5px] text-muted-foreground mt-1.5 line-clamp-2">{t.details}</div>}
                    <div className="flex items-center gap-2 text-[10.5px] text-muted-foreground mt-1.5">
                      {t.assigned_to_name && <span className="inline-flex items-center gap-1"><User size={11} /> {t.assigned_to_name}</span>}
                      {t.age_minutes != null && <span className="inline-flex items-center gap-1"><Clock size={11} /> open {fmtAge(t.age_minutes)}</span>}
                    </div>
                    {t.sla_breached && <div className="text-[10.5px] text-red-600 font-medium mt-1 inline-flex items-center gap-1"><Warning size={12} /> SLA breached</div>}
                    {col.key !== "resolved" && (
                      <div className="mt-2.5 space-y-1.5">
                        <Select value={assigneeValue(t)} onValueChange={(v) => assign.mutate({ id: t.id, assignee: v === "unassigned" ? null : v })}>
                          <SelectTrigger className="h-8 text-[12px]" data-testid={`sos-assign-${t.id}`}><SelectValue placeholder="Assign…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {agents.map((a) => <SelectItem key={`u${a.id}`} value={`user:${a.id}`}>{a.name} (team)</SelectItem>)}
                            {workers.map((w) => <SelectItem key={`w${w.id}`} value={`worker:${w.id}`}>{w.name} (worker)</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <div className="flex gap-1.5">
                          {NEXT[t.status] && <Button size="sm" variant="secondary" className="h-7 text-[11px] flex-1" data-testid={`sos-advance-${t.id}`} onClick={() => setStatus.mutate({ id: t.id, status: NEXT[t.status] })}>→ {NEXT[t.status].replace("_", " ")}</Button>}
                          <Button size="sm" className="h-7 text-[11px] flex-1" data-testid={`sos-resolve-${t.id}`} onClick={() => setStatus.mutate({ id: t.id, status: "resolved" })}>Resolve</Button>
                        </div>
                      </div>
                    )}
                    {col.key === "resolved" && <div className="text-[10.5px] text-success mt-2 inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Resolved</div>}
                  </div>
                ))}
                {!items.length && <div className="text-[11.5px] text-muted-foreground/60 text-center py-6">No requests</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Page>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  return (
    <div className="bg-card rounded-lg border border-border p-4 flex items-center gap-3">
      <div className={`h-9 w-9 rounded-md flex items-center justify-center ${tone === "danger" ? "bg-red-100 text-red-600" : "bg-primary/10 text-primary"}`}><Icon size={18} weight="fill" /></div>
      <div><div className="text-2xl font-heading font-semibold leading-none">{value}</div><div className="text-[11.5px] text-muted-foreground mt-1">{label}</div></div>
    </div>
  );
}

function fmtAge(m) {
  if (m == null) return "";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
}

function NewRequestDialog({ requestTypes, agents, workers, onDone }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("text");
  const [f, setF] = useState({ room_number: "", message: "", request_type_id: "", quantity: 1, priority: "normal", assignee: "unassigned" });
  const create = useMutation({
    mutationFn: async () => {
      const body = { room_number: f.room_number, assignee: f.assignee === "unassigned" ? null : f.assignee };
      if (mode === "text") body.message = f.message;
      else { body.request_type_id = f.request_type_id; body.quantity = Number(f.quantity) || 1; body.priority = f.priority; }
      return (await api.post("/soysos/tickets", body)).data;
    },
    onSuccess: () => { toast.success("Request created"); setOpen(false); setF({ room_number: "", message: "", request_type_id: "", quantity: 1, priority: "normal", assignee: "unassigned" }); onDone?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create request"),
  });
  const canSubmit = f.room_number && (mode === "text" ? f.message.trim() : f.request_type_id);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-request-btn" className="gap-1.5"><Plus size={16} weight="bold" /> New request</Button></DialogTrigger>
      <DialogContent data-testid="new-request-dialog">
        <DialogHeader><DialogTitle>Log a request</DialogTitle><DialogDescription>Create a request on behalf of a room — from a quick description or a specific type.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label className="text-xs">Room number</Label><Input value={f.room_number} onChange={(e) => setF({ ...f, room_number: e.target.value })} data-testid="nr-room" className="mt-1" placeholder="412" /></div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={mode === "text" ? "default" : "outline"} onClick={() => setMode("text")} data-testid="nr-mode-text">Describe it (AI)</Button>
            <Button type="button" size="sm" variant={mode === "type" ? "default" : "outline"} onClick={() => setMode("type")} data-testid="nr-mode-type">Pick a type</Button>
          </div>
          {mode === "text" ? (
            <div><Label className="text-xs">What does the guest need?</Label><Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={3} data-testid="nr-message" className="mt-1" placeholder="e.g. 2 extra towels and the AC isn't cooling" /></div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Request type</Label>
                <Select value={f.request_type_id} onValueChange={(v) => setF({ ...f, request_type_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="nr-type"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{requestTypes.map((rt) => <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Quantity</Label><Input type="number" min={1} value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="nr-qty" className="mt-1" /></div>
              <div><Label className="text-xs">Priority</Label>
                <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                  <SelectTrigger className="mt-1" data-testid="nr-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div><Label className="text-xs">Assign to (optional)</Label>
            <Select value={f.assignee} onValueChange={(v) => setF({ ...f, assignee: v })}>
              <SelectTrigger className="mt-1" data-testid="nr-assignee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {agents.map((a) => <SelectItem key={`u${a.id}`} value={`user:${a.id}`}>{a.name} (team)</SelectItem>)}
                {workers.map((w) => <SelectItem key={`w${w.id}`} value={`worker:${w.id}`}>{w.name} (worker)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending} data-testid="nr-submit">{create.isPending ? "Creating…" : "Create request"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
